import { ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useLocale } from '@hooks/useLocale';
import { RichTextField, LinkField } from '@prismicio/client';
import { NavUIContextProvider } from '@context/MainNavContext/NavigationState';
import Header from '@components/global/Header';
import Container from '@components/global/Container';
import Sidebar from '@components/global/Sidebar';
import Button from '@components/ui/Button';
import { useUI } from '@context/GlobalUIContext';
import { useCartUI } from '@context/CartContext';
import { MecProduct } from '@adapters/bigcommerce/types/product';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import { Store } from '@adapters/prismic/fetching/stores/types';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { ProductTagInfo } from '@adapters/prismic/types/productTagInfo';
import {
  FEATURE_FLAG_ALBY_CHATBOT,
  FEATURE_FLAG_ALBY_CHATBOT_BUTTON,
  FEATURE_FLAG_NEW_FOOTER,
} from '@config/feature-flag';
import trackAlbyEvent, { AlbyEventName } from '@utils/gtm/ga4/trackAlbyEvents';

import styles from './Layout.module.scss';
import { PromoBarDocumentData } from 'prismicio-types';

const Footer = dynamic(() => import('@components/global/Footer'));
const NewFooter = dynamic(() => import('_components/Footer'));
const BannerStripGlobal = dynamic(
  () => import('@components/global/BannerStripGlobal')
);

const CartSidebarView = dynamic(
  () => import('@components/cart/CartSidebarView')
);
const StoresModal = dynamic(
  () => import('../MyFavouriteStore/StoresModal/StoresModal')
);
const EmployeePricingModal = dynamic(
  () => import('@components/auth/EmployeePricingModal')
);
const LoginModal = dynamic(() => import('@components/auth/LoginModal'));
const PorductInfoModal = dynamic(
  () => import('@components/ui/Tags/TagInfoModal')
);

const ValuePropsContainer = dynamic(
  () => import('_components/ValuePropsContainer')
);
const VideoModal = dynamic(() => import('@components/ui/VideoModal'));

const MarketplaceSellerModal = dynamic(
  () =>
    import('@components/global/MarketplaceSellerModal/MarketplaceSellerModal')
);

interface LayoutProps {
  children: ReactNode;
  pageProps: {
    preview: boolean;
    alternateLocaleUrl?: string;
    langAltUrl?: string;
    product: MecProduct;
    promoMessages: PromoTicker[];
    stores: Store[];
    productTagInfoData: ProductTagInfo;
    hideTopRibbonAndStoreSelector?: boolean;
    promoBarData?: PromoBarDocumentData['messages'];
    bannerStripData?: {
      sliceData: {
        title: RichTextField | null;
        subtitle: RichTextField | null;
        cta_text: string | null;
        cta_url: LinkField | null;
        icon_name: string | null;
        icon_type: string | null;
        phosphor_icon: string | null;
        phosphor_weight: string | null;
        variant: 'membership' | 'light';
      } | null;
    } | null;
  };
}
interface AlbyButtonClickedEvent extends CustomEvent {
  detail: {
    widgetId: string;
  };
}

const Layout = ({ children, pageProps }: LayoutProps) => {
  const loadFooterRef = useRef<any>(null);
  const loadFooterValue = useObjectVisibility(loadFooterRef, 0.05);
  const [isLoadFooterRef, setIsLoadFooterRef] = useState(false);

  useEffect(() => {
    if (!isLoadFooterRef) setIsLoadFooterRef(loadFooterValue);
  }, [isLoadFooterRef, loadFooterValue]);

  const {
    displayCartFlyout,
    closeCartFlyout,
    displayTagInfoModal,
    displayLoginModal,
    displayEmployeePricingModal,
    displayStoresModal,
    displayVideoModal,
    displayMarketplaceSellerModal,
    videoId,
    videoHeight,
    videoWidth,
    sellerName,
  } = useUI();
  const { clearCartMessaging, setShowSPUCartError } = useCartUI();

  const router = useRouter();
  const locale = useLocale();

  const handleFlyoutClose = useCallback(() => {
    clearCartMessaging();
    closeCartFlyout();
    setShowSPUCartError(null);
  }, [clearCartMessaging, closeCartFlyout, setShowSPUCartError]);

  useEffect(() => {
    const handleRouteChange = () => {
      handleFlyoutClose();
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.query, handleFlyoutClose]);

  useEffect(() => {
    const handleButtonClicked = (e: Event) => {
      const { widgetId } = (e as AlbyButtonClickedEvent).detail;
      if (widgetId) {
        trackAlbyEvent({
          eventName: AlbyEventName.FLOATING_CLICKED,
          path: window.location.pathname,
        });
      }
    };

    document.addEventListener(
      'alby:Chatbot::LauncherButtonClicked',
      handleButtonClicked
    );

    return () => {
      document.removeEventListener(
        'alby:Chatbot::LauncherButtonClicked',
        handleButtonClicked
      );
    };
  }, []);

  const { t } = useTranslation();
  // Explicitly hide alby for: homepage, PDP, cart, auth interstital
  const shouldShowAlbyButton =
    !/^(\/$|.*\/(product\/|cart|auth\/))/.test(router.pathname) &&
    FEATURE_FLAG_ALBY_CHATBOT_BUTTON;

  return (
    <>
      <div>
        {pageProps.preview && (
          <div className={styles.previewBar}>
            <Container className={styles.previewBarInner}>
              <strong>Currently viewing in preview mode</strong>
              <Button
                href="/api/exit-preview"
                label="Exit preview"
                variant="secondary"
                size="small"
                icon="exit"
              />
            </Container>
          </div>
        )}
        <NavUIContextProvider>
          <Header
            alternateLocaleUrl={
              pageProps.alternateLocaleUrl || pageProps.langAltUrl
            }
            promoTickerMessages={pageProps.promoMessages}
            hideTopRibbonAndStoreSelector={
              pageProps.hideTopRibbonAndStoreSelector
            }
            promoBarData={pageProps.promoBarData}
          />
        </NavUIContextProvider>

        <main id="main-content" ref={loadFooterRef}>
          {children}
        </main>

        {isLoadFooterRef && (
          <>
            {FEATURE_FLAG_NEW_FOOTER ? (
              <>
                <BannerStripGlobal bannerData={pageProps.bannerStripData} />
                <ValuePropsContainer />
                <NewFooter />
              </>
            ) : (
              <Footer />
            )}
          </>
        )}

        <Sidebar
          open={displayCartFlyout}
          onClose={handleFlyoutClose}
          classNames={styles.sideBarStylesCart}
          label={t('cart.title')}
        >
          <CartSidebarView />
        </Sidebar>

        {displayLoginModal && <LoginModal />}
        {displayEmployeePricingModal && <EmployeePricingModal />}
        {displayStoresModal && <StoresModal />}
        {displayTagInfoModal && (
          <PorductInfoModal
            productTagInfoBannerData={pageProps?.productTagInfoData}
          />
        )}
        {displayVideoModal && (
          <VideoModal
            videoId={videoId}
            height={videoHeight}
            width={videoWidth}
          />
        )}
        {displayMarketplaceSellerModal && (
          <MarketplaceSellerModal sellerName={sellerName} />
        )}
      </div>
      {locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT && (
        <>
          <alby-chatbot
            widget-id={process.env.NEXT_PUBLIC_ALBY_CHATBOT_WIDGET_ID}
          ></alby-chatbot>
          {shouldShowAlbyButton && (
            <div className={styles.albyChatbotButton}>
              <alby-single-button-embed
                widget-id={process.env.NEXT_PUBLIC_ALBY_CHATBOT_BUTTON}
              ></alby-single-button-embed>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Layout;
